import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Globe, MailCheck, ShieldCheck, Zap } from "lucide-react";
import { API } from "@/config/site";
import { useStore } from "@/context/StoreContext";
import { FALLBACK_PRODUCTS } from "@/data/catalogFallback";
import ProductCard from "@/components/ProductCard";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";

const HERO_LINES = ["Digital gift cards,", "without the friction."];

const CHAPTERS = [
  { n: "01", title: "Choose your card", body: "Pick from five launch brands, select a denomination, and confirm the exact redemption region before anything else. No surprises after payment." },
  { n: "02", title: "Pay securely", body: "Checkout runs on Razorpay with nine display currencies. Guest checkout is the default — no account required, ever." },
  { n: "03", title: "Receive your code", body: "Your digital code is verified and delivered by email — usually within a few hours of payment." },
];

function ActiveHomePromotions({ store }) {
  const promotions = (store.banners || [])
    .filter((b) => b.enabled && (b.placement === "home" || b.placement === "both"))
    .filter((b) => b.image_url)
    .filter((b) => !b.start_at || Date.parse(b.start_at) <= Date.now())
    .filter((b) => !b.end_at || Date.parse(b.end_at) >= Date.now())
    .sort((a, b) => (a.market === store.market ? -1 : a.market === "all" ? 0 : 1) - (b.market === store.market ? -1 : b.market === "all" ? 0 : 1))
    .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0))
    .slice(0, 4);
  const [active, setActive] = useState(0);
  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => {
    if (promotions.length < 2) return;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % promotions.length);
    }, 1800);
    return () => window.clearInterval(timer);
  }, [promotions.length]);

  useEffect(() => {
    if (active >= promotions.length && promotions.length) setActive(0);
  }, [active, promotions.length]);

  if (!promotions.length) return null;
  const safeIndex = active < promotions.length ? active : 0;

  const goTo = (index) => {
    if (!promotions.length) return;
    setActive((index + promotions.length) % promotions.length);
  };

  const handleTouchStart = (event) => {
    setTouchStart(event.touches[0]?.clientX ?? null);
  };

  const handleTouchEnd = (event) => {
    if (touchStart === null) return;
    const endX = event.changedTouches[0]?.clientX ?? touchStart;
    const delta = endX - touchStart;
    if (Math.abs(delta) > 45) goTo(safeIndex + (delta < 0 ? 1 : -1));
    setTouchStart(null);
  };

  return (
    <section className="vault-container pt-12 sm:pt-16" data-testid="home-promotions">
      <div
        className="group relative overflow-hidden rounded-[1.75rem] border border-white/[0.08] bg-black shadow-[0_30px_100px_rgba(0,0,0,0.35)] touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex transition-transform duration-500 ease-out will-change-transform"
          style={{ transform: `translateX(-${safeIndex * 100}%)` }}
        >
          {promotions.map((promotion) => (
            <Link
              key={promotion.id}
              to={promotion.cta_url || "/cards"}
              aria-label={promotion.cta_label || promotion.text || "View promotion"}
              className="block min-w-full shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
            >
              <img
                src={promotion.image_url}
                alt={promotion.text || "ZEYINX gaming promotion"}
                className="block h-auto w-full object-contain"
                draggable="false"
              />
            </Link>
          ))}
        </div>

        {promotions.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous promotion"
              onClick={() => goTo(safeIndex - 1)}
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/45 text-white backdrop-blur transition hover:bg-black/65"
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Next promotion"
              onClick={() => goTo(safeIndex + 1)}
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/45 text-white backdrop-blur transition hover:bg-black/65"
            >
              ›
            </button>
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/35 px-2.5 py-1.5 backdrop-blur">
              {promotions.map((promotion, index) => (
                <button
                  key={promotion.id}
                  type="button"
                  aria-label={`Show promotion ${index + 1}`}
                  onClick={() => goTo(index)}
                  className={`h-1.5 rounded-full transition-all ${index === safeIndex ? "w-7 bg-white" : "w-1.5 bg-white/45 hover:bg-white/75"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

const ASSURANCES = [
  { icon: Zap, title: "Usually instant", body: "Most codes arrive within moments of payment." },
  { icon: ShieldCheck, title: "Secure by default", body: "Payments are handled end-to-end by Razorpay." },
  { icon: Globe, title: "Region-clear", body: "Every code shows exactly where it can be redeemed." },
  { icon: MailCheck, title: "Email delivery", body: "Codes land in your inbox and in your account." },
];

function HeroCards() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 110]);
  const cards = [
    { src: "/cards/playstation.svg", cls: "left-0 top-6 w-64 -rotate-[8deg]", d: 0 },
    { src: "/cards/xbox.svg", cls: "right-4 top-0 w-60 rotate-[7deg]", d: 0.5 },
    { src: "/cards/steam.svg", cls: "left-16 top-44 w-72 rotate-[2deg]", d: 1 },
    { src: "/cards/apple.svg", cls: "right-0 top-52 w-56 -rotate-[5deg]", d: 1.5 },
    { src: "/cards/nintendo.svg", cls: "left-40 top-[-24px] w-52 rotate-[12deg]", d: 2 },
  ];
  return (
    <motion.div style={{ y }} className="relative hidden h-[480px] lg:block" data-testid="hero-card-fan">
      {cards.map((c, i) => (
        <motion.div key={c.src} className={`absolute ${c.cls}`} initial={{ opacity: 0, y: 60, rotate: 0 }} animate={{ opacity: 1, y: [0, -12, 0] }} transition={{ opacity: { duration: 0.9, delay: 0.5 + i * 0.14 }, y: { duration: 6 + i, repeat: Infinity, ease: "easeInOut", delay: c.d } }}>
          <img src={c.src} alt="" className="w-full rounded-xl border border-white/10 shadow-[0_24px_60px_rgba(0,0,0,0.55)]" />
        </motion.div>
      ))}
    </motion.div>
  );
}

export default function Home() {
  const store = useStore();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!store.ready) return;
    const fallback = Object.values(store.productsMap || {}).length
      ? Object.values(store.productsMap)
      : FALLBACK_PRODUCTS;
    const params = { limit: 6 };
    if (store.market) params.market = store.market;
    axios.get(`${API}/products`, { params })
      .then((r) => setProducts(Array.isArray(r.data?.products) ? r.data.products : fallback.slice(0, 6)))
      .catch(() => setProducts(fallback.slice(0, 6)));
  }, [store.ready, store.market, store.productsMap]);

  const browseHref = store.market ? `/market/${store.market}` : "/cards";

  return (
    <div data-testid="home-page"><div className="neon-page">
      <section className="relative overflow-hidden"><div className="hero-grid absolute inset-0" /><div className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-primary/[0.07] blur-[140px]" /><div className="vault-container relative grid gap-10 pb-24 pt-20 sm:pt-28 lg:grid-cols-[1.05fr_1fr] lg:pb-32">
        <div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }} className="overline-label">Zeyinx — Digital gift-card store</motion.p>
          <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.06] tracking-tighter sm:text-5xl lg:text-6xl">{HERO_LINES.map((line, i) => <span key={line} className="block overflow-hidden pb-1"><motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.22 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}>{i === 1 ? <span className="text-primary">{line}</span> : line}</motion.span></span>)}</h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }} className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">Choose a card, pay securely, and receive your digital code.</motion.p>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }} className="mt-9 flex flex-wrap items-center gap-3"><Link to={browseHref} data-testid="hero-browse-button" className="group flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.03]">Browse gift cards<ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" /></Link><Link to="/how-it-works" data-testid="hero-how-it-works-button" className="rounded-full border border-white/15 px-6 py-3 text-sm text-muted-foreground transition-colors duration-200 hover:border-white/30 hover:text-foreground">How it works</Link></motion.div>
          {store.ready && <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 1.05 }} onClick={store.openMarketModal} data-testid="hero-region-pill" className="mt-8 flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-muted-foreground transition-colors duration-200 hover:border-white/25 hover:text-foreground"><span>{store.marketObj ? store.marketObj.flag : "🌐"}</span><span>{store.marketObj ? `Browsing the ${store.marketObj.name} marketplace` : "Choose your market"} · Prices in {store.currency}</span><span className="text-primary">Change</span></motion.button>}
        </div><HeroCards />
      </div></section>
      <Marquee />
      <section className="vault-container pt-24" data-testid="lineup-section"><Reveal><p className="overline-label">01 — The lineup</p><h2 className="mt-3 max-w-xl font-display text-3xl font-semibold tracking-tight sm:text-4xl">One marketplace. Zero friction.</h2><p className="mt-4 max-w-lg text-sm leading-relaxed text-muted-foreground">Every card lists its exact redeemable countries upfront, converts to your currency instantly, and lands in your inbox minutes after payment.</p></Reveal><div className="mt-12 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">{products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}</div></section>
      <section className="vault-container pt-32" data-testid="manifesto-section"><Reveal><p className="overline-label">02 — The Zeyinx way</p></Reveal><div className="mt-6 flex flex-col">{CHAPTERS.map((c, i) => <Reveal key={c.n} delay={i * 0.08}><div className="grid gap-4 border-t border-white/[0.07] py-12 sm:grid-cols-[120px_1fr_1.2fr] sm:items-baseline"><span className="font-display text-5xl font-semibold tracking-tight text-white/[0.08]">{c.n}</span><h3 className="font-display text-2xl font-semibold tracking-tight">{c.title}</h3><p className="max-w-md text-sm leading-relaxed text-muted-foreground">{c.body}</p></div></Reveal>)}</div></section>
      <section className="vault-container pt-20"><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{ASSURANCES.map((a, i) => <Reveal key={a.title} delay={i * 0.07}><div className="rounded-2xl border border-white/[0.08] bg-card p-6"><a.icon className="h-5 w-5 text-primary" /><p className="mt-4 font-display text-sm font-semibold tracking-tight">{a.title}</p><p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{a.body}</p></div></Reveal>)}</div></section>
      <section className="vault-container pt-28"><Reveal><div className="relative overflow-hidden rounded-3xl border border-white/[0.08] bg-card px-8 py-16 text-center sm:py-20"><div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-[560px] -translate-x-1/2 rounded-full bg-primary/[0.09] blur-[110px]" /><h2 className="relative font-display text-3xl font-semibold tracking-tight sm:text-4xl">Ready when you are.</h2><p className="relative mx-auto mt-4 max-w-md text-sm text-muted-foreground">Verified and delivered by email after payment — usually within a few hours.</p><Link to={browseHref} data-testid="cta-browse-button" className="relative mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.03]">Browse gift cards<ArrowRight className="h-4 w-4" /></Link></div></Reveal></section>
    </div>
    </div>
  );

}
